import { Device } from "@fairfleet/geotab";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAINTENANCE_PRIORITY,
  MAINTENANCE_PRIORITY_LABEL_KEYS,
  MaintenancePriorityCode,
} from "@/constants/MaintenancePriority";
import { useCreateMaintenanceWorkOrder } from "@/hooks/maintenance/useCreateMaintenanceWorkOrder";
import { WarningType } from "@/models/Warning";

/** Persisted EventType name shared by all telltale check orders. Stored data, not localized. */
const TELLTALE_ORDER_TITLE = "Bitte Überprüfen";

const PRIORITY_OPTIONS: MaintenancePriorityCode[] = [
  MAINTENANCE_PRIORITY.LOW,
  MAINTENANCE_PRIORITY.MEDIUM,
  MAINTENANCE_PRIORITY.HIGH,
  MAINTENANCE_PRIORITY.CRITICAL,
];

export interface ActiveTellTale {
  id: string;
  /** Raw telltale constant name, e.g. "TellTale – Engine oil (0 = off ...)". */
  name: string;
  status: number;
}

export interface CreateTellTaleOrderDialogProps {
  open: boolean;
  device: Device;
  warningType: WarningType;
  ruleId: string;
  activeTellTales: ActiveTellTale[];
  onClose: () => void;
}

/** "TellTale – {name} (0 = off ...)" → "{name}" */
const cleanName = (name: string) => name.split("–")[1]?.split("(")[0].trim() ?? name;

export function CreateTellTaleOrderDialog(props: CreateTellTaleOrderDialogProps) {
  const { open, device, warningType, ruleId, activeTellTales, onClose } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync, isPending } = useCreateMaintenanceWorkOrder();

  const textFor = (ids: Set<string>) =>
    activeTellTales
      .filter(tt => ids.has(tt.id))
      .map(tt => cleanName(tt.name))
      .join(", ");

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(activeTellTales.map(tt => tt.id)),
  );
  const [touched, setTouched] = useState(false);
  const [text, setText] = useState(() => textFor(new Set(activeTellTales.map(tt => tt.id))));
  const [priorityCode, setPriorityCode] = useState<MaintenancePriorityCode>(
    MAINTENANCE_PRIORITY.HIGH,
  );

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    if (!touched) setText(textFor(next));
  };

  const handleSubmit = async () => {
    try {
      await mutateAsync({
        device,
        ruleId,
        pmTitle: TELLTALE_ORDER_TITLE,
        pmDescription: text,
        warningType,
        priorityCode,
        description: text,
        notes: text,
      });
      enqueueSnackbar(t("createPmOrder.success"), { variant: "success" });
      onClose();
    } catch (err) {
      const stage = (err as { stage?: string } | null)?.stage;
      if (stage === "job") {
        enqueueSnackbar(t("createPmOrder.errorJob"), { variant: "warning" });
        onClose();
      } else {
        enqueueSnackbar(t("createPmOrder.errorOrder"), { variant: "error" });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("tellTaleOrder.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("createPmOrder.device")}:
            </Typography>
            <Typography variant="body2">{device.name}</Typography>
          </Stack>

          <FormGroup>
            <FormLabel sx={{ mb: 0.5 }}>{t("tellTaleOrder.selectLabel")}</FormLabel>
            {activeTellTales.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("tellTaleOrder.noActive")}
              </Typography>
            ) : (
              activeTellTales.map(tt => (
                <FormControlLabel
                  key={tt.id}
                  control={
                    <Checkbox checked={selected.has(tt.id)} onChange={() => toggle(tt.id)} />
                  }
                  label={cleanName(tt.name)}
                />
              ))
            )}
          </FormGroup>

          <TextField
            label={t("tellTaleOrder.freeText")}
            multiline
            rows={3}
            value={text}
            onChange={e => {
              setTouched(true);
              setText(e.target.value);
            }}
          />

          <TextField
            select
            label={t("createPmOrder.priority")}
            value={priorityCode}
            onChange={e => setPriorityCode(Number(e.target.value) as MaintenancePriorityCode)}
          >
            {PRIORITY_OPTIONS.map(code => (
              <MenuItem key={code} value={code}>
                {t(MAINTENANCE_PRIORITY_LABEL_KEYS[code])}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t("createPmOrder.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending || text.trim() === ""}
        >
          {t("createPmOrder.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
